import React, {Dispatch, SetStateAction} from 'react';
import {CustomTypography} from "./CustomTypography";
import Paginationtable from "./pagination/PaginationTable";
import {Card} from "@material-tailwind/react";
import {useTranslation} from "react-i18next";
import {formatDateString} from "../../utils/daysOptions";
import {Skeleton} from "./Skeleton";

export type TypeItemTable = {onClick?: () => void, row: (string | number | Date | React.ReactNode | undefined)[]};

export type CustomTableProps = {
    header: string[];
    loading?: boolean;
    pagination?: boolean;
    error?: string;
    number?: number; // pagination props
    totalPages?: number; // pagination props
    totalElements?: number; // pagination props
    listElement?: TypeItemTable[];
    highTable?: string;
    setIndex?: Dispatch<SetStateAction<number | undefined>>;
    classNameTable?: string;
    classNameHeader?: string;
    classNameItemHeader?: string;
    classNameBody?: string;
}

export default function CustomTable({
                                        header,
                                        loading,
                                        pagination,
                                        error,
                                        number,
                                        totalPages,
                                        totalElements,
                                        listElement,
                                        highTable,
                                        classNameTable,
                                        classNameHeader,
                                        classNameItemHeader,
                                        classNameBody,
                                        setIndex,
}: CustomTableProps) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const createContentLoading = () => (
        [...Array(4).keys()].map((row) => (
            <tr key={row}>
                {[...Array(1).keys()].map((index) => (
                    <td key={`${row}-${index}`} colSpan={header.length}>
                        <Skeleton className={`mx-2 mt-2 ${row === 3 ? 'mb-2' : ''}`}/>
                    </td>
                ))}
            </tr>
        ))
    );

    function getStyleRow(index: number, element: string | Date | number | React.ReactNode | undefined) {
        if (typeof element !== 'number' && typeof element !== 'string' && !(element instanceof Date)) {
            return ''
        }
        const style = `p-2 ${element instanceof Date ? 'text-center' : typeof element === 'number' ? 'text-right' : ''}`;
        if (index === 0) {
            return `${classNameBody} ${style} border-r border-blue-gray-50`
        }
        if ((index + 1) === header.length) {
            return `${classNameBody} ${style} border-l border-blue-gray-50`
        }
        return `${classNameBody} ${style} border-x border-blue-gray-50`
    }

    const row = (items : (string | number | Date | React.ReactNode | undefined)[]) => {
        if (Array.isArray(items)) {
        return (
            items && items.map((element: string | number | Date | React.ReactNode | undefined, innerIndex: number) => {
                return (
                    <td key={innerIndex} className={getStyleRow(innerIndex, element)}>
                        {element instanceof Date || element === undefined ?
                            <CustomTypography text={formatDateString(element)} />
                            :
                            typeof element === 'number' || typeof element === 'string' ? (
                                <CustomTypography text={element} />
                            ) : (
                                element as React.ReactNode // Afirmación de tipo explícita
                            )}
                    </td>
                );
            })
        )} else {
            return null;
        }
    }
    const bodyTable = () => {
        if (listElement === undefined) {
            return <tr>
                <td className={'text-center'} colSpan={header.length}>-</td>
            </tr>
        }

        if (error) {
            return (
                <tr>
                    <td colSpan={header.length} className="py-3 text-center">
                        {error}
                    </td>
                </tr>
            )
        }

        if (totalElements === 0 || (listElement && listElement.length === 0)) {
            return (
                <tr>
                    <td colSpan={header.length} className="py-3 text-center text-[14px]">
                        {t('empty-records')}
                    </td>
                </tr>
            );
        }

        return listElement && listElement.map((items: TypeItemTable, index: number) => {
            const isLast = (index + 1) === listElement.length;
            return (
                <tr
                    key={index}
                    className={`${items.onClick ? 'cursor-pointer' : ''} 
                    ${classNameBody} bg-transparent ${isLast ? '' : 'border-b border-blue-gray-50'}`}
                    onClick={() => {if(items.onClick) items.onClick()}}
                >
                    {row(items.row)}
                </tr>
            );
        })
    }

    return (
        <Card placeholder="card" className={` rounded-[6px] border-[1px] border-[#CBD6E2] flex flex-col ${classNameTable}`}>
            <div className={`overflow-y-auto bg-transparent`}
                 style={{maxHeight: highTable ? highTable : '300px'}}
            >
                <table className="w-full min-w-max table-auto text-left bg-transparent">
                    <thead className="sticky top-0 bg-transparent">
                        <tr className={classNameHeader}>
                            {header.map((head, index) => (
                                <th key={head} className={`${classNameItemHeader} 
                                ${index === 0 ? 'rounded-tl-[6px]' : ''} 
                                ${(index + 1) === header.length ? 'rounded-tr-[6px]' : ''} 
                                !border-b-[1px] border-b-[#CBD6E2] bg-headerTable py-2 px-2 z-1 
                                font-light text-gray-500 leading-none text-center text-[12px]
                                `}>{head}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                    {loading ? createContentLoading() : bodyTable()}
                    </tbody>
                </table>
            </div>
            {pagination && <Paginationtable
                currentPage={number}
                totalPages={totalPages}
                totalElements={totalElements}
                {...{ setIndex }} />}
        </Card>
    );
}
